import loading from 'assets/pictures/loading.gif'
import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { orderBy, sortBy } from 'lodash'
import QualityHeatMap from '../../home/components/graph/QualityHeatMap'
import DtoSimpleStation from 'home/dto/DtoSimpleStation'
import Card from '../../components/card/Card'
import Table from '../../components/datatable/Table'
import { getDate } from '../../utils/DateUtil'
import MonitoredMaterielPortletPanel from '../../materiel/components/dashboard/MonitoredMaterielPortletPanel'
import ProgressCard from '../../components/card/ProgressCard'
import { getUserBookmarksByStationType } from '../../utils/UserUtil'
import { componentHasHabilitations } from '../../utils/HabilitationUtil'
import {
    H_MAT_MODULE,
    H_QUALITO_MODULE,
} from '../../account/constants/AccessRulesConstants'
import HabilitationRequired from '../../utils/components/HabilitationRequired'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UserThunk from 'administration/components/user/actions/UserThunk'
import QualityThunk from 'quality/actions/QualityThunk'
import CampaignThunk from 'campaign/actions/CampaignThunk'
import useStateProgress from 'utils/customHook/useStateProgress'
import { CampaignsPanel } from './qualitometers/QualitometersDashboardApp'
import { Grid2 } from '@mui/material'
import { NewTable } from '../../components/datatable/NewTable'

const panelTable = 'table'
const panelGraph = 'graph'

const QualityCard = () => {
    const {
        userBookmarks,
        qualitometers,
        qualityCampaigns,
        monitoredMateriels,
        citiesIndex,
        userBookmarksStatus,
        qualitometersListSpecificStatus,
        qualityCampaignsStatus,
        contacts,
        monitoredStations,
    } = useSelector(store => ({
        userBookmarks: store.UserReducer.userBookmarks,
        qualitometers: store.QualityReducer.qualitometersListSpecific,
        qualityCampaigns: store.QualityReducer.qualityCampaigns,
        monitoredMateriels: store.MaterielReducer.monitoredMateriels,
        citiesIndex: store.CityReducer.citiesIndex,
        userBookmarksStatus: store.DataManagerReducer.user.userBookmarksStatus,
        qualitometersListSpecificStatus: store.DataManagerReducer.quality.qualitometersListSpecificStatus,
        qualityCampaignsStatus: store.DataManagerReducer.campaign.qualityCampaignsStatus,
        contacts: store.ContactReducer.contacts,
        monitoredStations: store.EventsReducer.monitoredStations,
    }), shallowEqual)

    const { progress: progressBar, isLoaded: dataLoaded } = useStateProgress([
        userBookmarksStatus,
        qualitometersListSpecificStatus,
        qualityCampaignsStatus,
    ])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(UserThunk.fetchBookmarks())
        dispatch(QualityThunk.fetchQualitometersListSpecific())
        dispatch(CampaignThunk.fetchQualityCampaigns())
    }, [])

    const [panelType, setPanelType] = useState(panelTable)

    const bookmarks = useMemo(() => getUserBookmarksByStationType(userBookmarks, STATION_TYPE_NAME.quality), [userBookmarks])

    const stationsForTable = useMemo(() => bookmarks.reduce((acc, code) => {
        const bookmarksStations = bookmarks.map(code => qualitometers.find(s => s.code === code)).filter(p => !!p).map(s => ({ ...s, isBookmark: true }))
        const monitored = monitoredStations.filter(s => s.type === STATION_TYPE_CONSTANT.QUALITY && qualitometers.some(s2 => s.id === s2.id))
            .map(s => ({ ...s, isMonitored: true, ...qualitometers.find(s2 => s.id === s2.id) }))
        const reduced = [...bookmarksStations, ...monitored].reduce((acc, s) => {
            const found = acc.find(s2 => s.id === s2.id)
            if (found) {
                return [...acc.filter(s2 => s2.id !== s.id), { ...s, ...found }]
            }
            return [...acc, s]
        }, [])
        return orderBy(reduced, [s => s.isMonitored && s.isBookmark ? 2 : (s.isMonitored ? 1 : 0), 'name'], ['desc', 'asc'])
    }, []), [qualitometers, bookmarks, monitoredStations])

    const data = useMemo(() => ({
        stations: qualitometers,
        bookmarks,
        icon: 'pie_chart',
        campaigns: qualityCampaigns,
        stationType: STATION_TYPE_CONSTANT.QUALITY,
        habilitation: componentHasHabilitations(H_QUALITO_MODULE),
        monitoredMateriels: monitoredMateriels.filter(m => m.typeSite === STATION_TYPE_CONSTANT.QUALITY),
    }), [bookmarks, qualityCampaigns, contacts, monitoredMateriels, qualitometers])

    const bookmarksStationPanel = useMemo(() => {
        if (!stationsForTable.length) {
            return null
        }
        const tableData = sortBy(stationsForTable.map(station => {
            const cityFound = citiesIndex[station.townCode]
            const result = new DtoSimpleStation(station)
            return {
                ...result,
                city: cityFound ? `${cityFound.name} [${cityFound.code}]` : station.townCode,
                historic: station.historyStart && station.historyEnd ? `${getDate(station.historyStart)} ${i18n.to} ${getDate(station.historyEnd)}` : '',
                operations: station.operationCount,
                comment: station.eventComment,
                nullValue: {
                    value: '',
                    align: 'center',
                    leftIcon: station.isMonitored && station.isBookmark ? {
                        icon:  'notificationsNone',
                        color: 'red',
                        tooltip: i18n.oversight,
                    } : undefined,
                    rightIcon: {
                        icon: station.isBookmark ? 'star' : 'notificationsNone',
                        color: station.isBookmark ? 'orange' : 'red',
                        tooltip: station.isBookmark ? i18n.bookmark : i18n.oversight,
                    },
                },
            }
        }), 'name')
        if (panelType === panelTable) {
            return (
                <NewTable
                    headers={ ['nullValue', 'code', 'city', 'name', 'stationType', 'historic', 'operations', 'comment'] }
                    rows={tableData}
                    onClickRow={s => dispatch(push(`/station/quality/${s.id}`))}
                />
            )
        }
        return (
            <Card
                title={(
                    <a>
                        <i className='waves-effect material-icons'>grade</i>
                        {i18n.myFavourites} ({stationsForTable.length} {i18n.elements})
                    </a>
                )}
                actions={[{
                    iconName: 'list',
                    onClick: () => setPanelType(panelTable),
                }]}
            >
                <QualityHeatMap qualitometers={stationsForTable} />
            </Card>
        )
    }, [citiesIndex, stationsForTable, panelType])

    const bookmarksPanel = useMemo(() => {
        if (data?.bookmarks?.length > 0 && data?.stations?.length > 0) {
            return (
                <div className='row no-margin'>
                    <div className='section no-padding'>
                        <div className='row no-margin'>
                            {bookmarksStationPanel}
                        </div>
                    </div>
                </div>
            )
        }
        return null
    }, [bookmarksStationPanel, data?.bookmarks?.length, data?.stations?.length])

    const campaignsPanel = useMemo(() => {
        if (data.campaigns?.length > 0) {
            return (
                <CampaignsPanel campaigns={data.campaigns} />
            )
        }
        return null
    }, [data.campaigns])

    if (dataLoaded) {
        if (!(data.habilitation && ((data.bookmarks.length > 0 && data.stations.length > 0) || data.campaigns.length > 0 || data.monitoredMateriels.length > 0 || data?.exploitations?.length > 0)) || !(bookmarksPanel || campaignsPanel)) {
            return null
        }
        return (
            <div className='row no-margin padding-top-1'>
                <Card
                    title={(
                        <a className='waves-effect' onClick={() => dispatch(push(`/${data.redirect || STATION_TYPE_NAME.quality}`))}>
                            <i className='material-icons '>{data.icon}</i>
                            {i18n[STATION_TYPE_NAME.quality]}
                        </a>
                    )}
                >
                    <Grid2 container>
                        <Grid2 size={12}>
                            {bookmarksPanel}
                        </Grid2>
                        <Grid2 size={12}>
                            {campaignsPanel}
                        </Grid2>
                        <HabilitationRequired habilitation={H_MAT_MODULE}>
                            <MonitoredMaterielPortletPanel noHeader={true} stationType={data.stationType} />
                        </HabilitationRequired>
                    </Grid2>
                </Card>
            </div>
        )
    }
    return (
        <div className='padding-top-1'>
            <Card>
                <div className='row no-margin center-align padding-top-5'>
                    <img src={loading} />
                    <ProgressCard withoutCard message={i18n.qualityCardInProgress} progress={progressBar} />
                </div>
            </Card>
        </div>
    )
}

export default QualityCard